import jwtDecode from 'jwt-decode';
import {
  has, get, keys, times, uniq, reduce,
  camelCase,
} from 'lodash';
import units from '../../lib/units';
import chart from './chart';
import { LOCATION_MODES, DEFAULT_INPUTS, totalSavings } from './footprint';
import params from './params';
import results from './results';
import * as user from './user';
import * as leaders from './leaders';
import inputs, { getUiInput, getEnsuredNumericInput, getNumericDefault, ontarioMetricDefaults } from './inputs';
import numericInput from '../../lib/numericInput';
import { getLocaleFromWindowLocation } from '../../lib/routerHelper';
import * as locationAutocomplete from './locationAutocomplete.ts';

const isGlobalMenuVisible = state => state.ui.isGlobalMenuVisible;

const locale = _state => getLocaleFromWindowLocation();

const widgetExists = state => state.ui.widget !== null;
const widgetWindow = state => get(state.ui, 'widget.window', null);
const widgetContainer = state => get(state.ui, ['widget', 'container'], null);
const widgetSource = state => get(state.ui, ['widget', 'options', 'source'], '').toLowerCase();
const widgetOptions = state => get(state.ui, ['widget', 'options'], {});

const token = state => state.auth.token;

const isTokenValid = (state) => {
  if (!token(state)) return false;
  try {
    return !jwtDecode(token(state)).exp || jwtDecode(token(state)).exp < Date.now();
  } catch (err) {
    return false;
  }
};

const inputIncome = state => getEnsuredNumericInput(state, 'input_income');

const inputSize = state => getEnsuredNumericInput(state, 'input_size');

const userTypedCanada = state => new RegExp(state.ui.inputLocation, 'i').test(/canada/);

const location = state => state.location;

const locationModes = (_state) => {
  const { ZIP_CODE, CITY, STATE, COUNTRY } = LOCATION_MODES;
  return [ZIP_CODE, CITY, STATE, COUNTRY];
};

const termsAndConditionsUrl = (state) => {
  if (state.ui.source === 'ontario') return '/assets/coolclimate-ontario-terms-of-use.pdf';
  return '/assets/coolclimate-terms-of-use.pdf';
};

const locationSuggestions = state => state.ui.locationSuggestions || [];

const userFootprintInitialized = state =>
  reduce(
    (state.user_footprint.compute_takeaction_results || {}),
    (previous, _value, key) => (!has(DEFAULT_INPUTS, key) ? previous + 1 : previous),
    0,
  ) > keys(DEFAULT_INPUTS).length;

const averageFootprintInitialized = state => reduce(
    get(state, 'average_footprint.get_defaults_and_results', {}),
    (previous, _value, key) => (!has(DEFAULT_INPUTS, key) ? previous + 1 : previous),
    0,
  ) > keys(DEFAULT_INPUTS).length;

const isFootprintInitialized = state => averageFootprintInitialized(state) &&
    userFootprintInitialized(state) &&
    !state.ui.loadingCalculatorAnswers;

const hasUserChangedInput = (state) => {
  if (!isFootprintInitialized(state)) return false;
  return keys(state.user_footprint.inputs)
    .some((key) => {
      const value = state.user_footprint.inputs[key];
      if (has(DEFAULT_INPUTS, key)) return false;
      if (widgetSource(state) === 'ontario' &&
        has(ontarioMetricDefaults, key) &&
        value === ontarioMetricDefaults[key]) return false;
      return true;
    });
};

const resultTakeActionTons = state =>
  // NOTE: This API key is inaccurate. It is reporting tons, not pounds.
  JSON.parse(get(state.user_footprint, ['compute_takeaction_results', 'result_takeaction_pounds']));

const resultTakeActionDollars = state =>
  JSON.parse(get(state.user_footprint, ['compute_takeaction_results', 'result_takeaction_dollars']));

const resultTakeActionNet10Yr = state =>
  JSON.parse(get(state.user_footprint, ['compute_takeaction_results', 'result_takeaction_net10yr']));

const allSavings = state => ({
  resultTakeActionTons: resultTakeActionTons(state),
  resultTakeActionDollars: resultTakeActionDollars(state),
  resultTakeActionNet10Yr: resultTakeActionNet10Yr(state),
});

const totalTonsSaved = state => totalSavings(state, resultTakeActionTons(state));

const totalDollarsSaved = state => totalSavings(state, resultTakeActionDollars(state));

const totalNet10YrCost = state => totalSavings(state, resultTakeActionNet10Yr(state));

const savings = (state, { t }) => [
  {
    key: 'totalTonsSaved',
    value: t('{{value}} tons saved', { value: numericInput.format(totalTonsSaved(state), { decimalPlaces: 1 }), ns: 'takeAction' }),
  },
  {
    key: 'totalDollarsSaved',
    value: t('{{value}} saved / year', {
      // eslint-disable-next-line no-template-curly-in-string
      value: t('${{amount}}', { amount: numericInput.format(totalDollarsSaved(state)) }),
      ns: 'takeAction',
    }),
  },
  {
    key: 'totalNet10YrCost',
    value: t('{{value}} upfront', {
      // eslint-disable-next-line no-template-curly-in-string
      value: t('${{amount}}', { amount: numericInput.format(totalNet10YrCost(state)) }),
      ns: 'takeAction',
    }),
  },
];

const pledges = state => state.pledges;

const isMetric = state => state.ui.unitSystem === 'metric';

const makeVehicle = (index, state, t) => {
  const isElectric = has(state.user_footprint, ['electricVehicles', `${index}`]);
  const annualDistance = getUiInput(state, `input_footprint_transportation_miles${index}`);
  const efficiency = getEnsuredNumericInput(state, `input_footprint_transportation_mpg${index}`);
  const fuelType = getEnsuredNumericInput(state, `input_footprint_transportation_fuel${index}`);
  let fuelTypeDisplay = fuelType === 1 ? t('Gasoline', { ns: 'travel' }) : t('Diesel', { ns: 'travel' });
  if (isElectric) fuelTypeDisplay = t('Electric', { ns: 'travel' });
  const defaultDistance = getNumericDefault(state, `input_footprint_transportation_miles${index}`);
  return {
    index,
    isElectric,
    annualDistance,
    defaultDistance: numericInput.format(defaultDistance),
    efficiency,
    efficiencyDisplay: numericInput.format(efficiency),
    fuelType,
    fuelTypeDisplay,
    fuelConsumptionUnit: units.fuelConsumptionUnit(t)(isMetric(state), isElectric),
    fuelEfficiencyUnit: units.fuelEfficiencyUnit(t)(isMetric(state), isElectric),
  };
};

const tradeInVehicleGenerator = tradeInVehicleKey => (state, { t }) => {
  const index = get(state.ui, ['tradeInVehicleIndices', tradeInVehicleKey], 1);
  return makeVehicle(parseInt(index, 10), state, t);
};

const electricTradeInVehicle = tradeInVehicleGenerator('electric');
const hybridTradeInVehicle = tradeInVehicleGenerator('hybrid');
const altFuelTradeInVehicle = tradeInVehicleGenerator('alternativefuel');
const moreEfficientTradeInVehicle = tradeInVehicleGenerator('more_efficient');
const rideMyBikeTradeInVehicle = tradeInVehicleGenerator('ride_my_bike');
const publicTransportationTradeInVehicle = tradeInVehicleGenerator('take_public_transportation');
const telecommuteTradeInVehicle = tradeInVehicleGenerator('telecommute_to_work');

const vehicles = (state, { t }) => {
  const numberVehicles = getEnsuredNumericInput(state, 'input_footprint_transportation_num_vehicles');
  return times(numberVehicles).map(n => makeVehicle(n + 1, state, t));
};

const canAddMoreVehicles = state => getEnsuredNumericInput(state, 'input_footprint_transportation_num_vehicles') < 10;

const isTravelUiSimple = state => getEnsuredNumericInput(state, 'input_footprint_transportation_groundtype') === 0;

const electricityUnits = (apiValue, t) => {
  switch (apiValue) {
    case 0: return t('$', { ns: 'units' });
    case 1: return t('kWh', { ns: 'units' });
    default: {
      throw new Error(`Unexpected value ${apiValue} for natural gas units`);
    }
  }
};
const naturalGasUnits = (apiValue, t, state) => {
  switch (apiValue) {
    case 0: return t('$');
    case 1: return t('therms', { ns: 'units' });
    case 2: return `${isMetric(state) ? t('m', { ns: 'units' }) : t('ft', { ns: 'units' })}<sup>3</sup>`;
    default: {
      throw new Error(`Unexpected value ${apiValue} for natural gas units`);
    }
  }
};
const heatingOilUnits = (apiValue, t, state) => {
  switch (apiValue) {
    case 0: return t('$');
    case 1: return isMetric(state) ? t('lt', { ns: 'units' }) : t('gal', { ns: 'units' });
    default: {
      throw new Error(`Unexpected value ${apiValue} for heating oil units`);
    }
  }
};

const unitOptions = (state, { unitApiKey, t }) => {
  switch (unitApiKey) {
    case 'input_footprint_housing_naturalgas_type': {
      return [
        { value: 0, display: naturalGasUnits(0, t, state) },
        { value: 1, display: naturalGasUnits(1, t, state) },
        { value: 2, display: naturalGasUnits(2, t, state) },
      ];
    }
    case 'input_footprint_housing_heatingoil_type': {
      return [
        { value: 0, display: heatingOilUnits(0, t, state) },
        { value: 1, display: heatingOilUnits(1, t, state) },
      ];
    }
    case 'input_footprint_housing_electricity_type': {
      return [
        { value: 0, display: electricityUnits(0, t, state) },
        { value: 1, display: electricityUnits(1, t, state) },
      ];
    }
    default: {
      throw new Error(`Unexpected unit options requested for ${unitApiKey}.`);
    }
  }
};

const unitsDisplay = (state, { t, unitApiKey }) => {
  const inputValue = getEnsuredNumericInput(state, unitApiKey);
  switch (unitApiKey) {
    case 'input_footprint_housing_naturalgas_type': {
      return naturalGasUnits(inputValue, t, state);
    }
    case 'input_footprint_housing_heatingoil_type': {
      return heatingOilUnits(inputValue, t, state);
    }
    case 'input_footprint_housing_electricity_type': {
      return electricityUnits(inputValue, t);
    }
    default: {
      throw new Error(`Unexpected units requested for ${unitApiKey}.`);
    }
  }
};

const inputValue = (state, { apiKey, inputValue: existing }) => {
  if (existing !== undefined) return existing;
  if (!has(inputs, camelCase(apiKey))) {
    throw new Error(`No selector for inputValue ${apiKey}.`);
  }
  return inputs[camelCase(apiKey)](state);
};

const unitValue = (state, { unitApiKey }) => inputs[camelCase(unitApiKey)](state);

const isMonthlyInput = (state, { apiKey }) => get(state.ui, ['monthlyInputs', apiKey], false);

const annualElectricityConsumption = (state) => {
  const apiKey = 'input_footprint_housing_electricity_kwh';
  const pullMonthlyValues = false;
  return numericInput.format(getEnsuredNumericInput(state, apiKey, { pullMonthlyValues }));
};

const actionsTaken = state => uniq([
  ...keys(get(state.user_footprint, ['actions', 'pledged'])),
  ...keys(get(state.user_footprint, ['actions', 'completed'])),
]).sort();

const modalDeleteUser = state => state.ui.modal === 'deleteUser';
const modalSaveUser = state => state.ui.modal === 'saveUser';
const toast = state => state.ui.toast;

const getCountryFromSelectedLocation = (state) => {
  try {
    return state.locationAutocomplete.get_started__input_location.inputDisplay;
  } catch (_err) {
    return undefined;
  }
};

const averageComparisonLocation = (state, { t }) => {
  try {
    if (state.average_footprint.inputs.input_location_mode === LOCATION_MODES.COUNTRY) {
      return getCountryFromSelectedLocation(state) || t('United States', { ns: 'geo' });
    }
  } catch (_err) {
    // pass
  }
  return state.ui.averageComparisonLocation || t('United States', { ns: 'geo' });
};

const spinner = state => state.ui.spinner;
const modalLeaderPledges = state => state.modalLeaderPledges;

export { default as pick } from 'lodash/pick';

export const bindSelectors = (selections, state, ownProps) =>
  reduce(
    selections,
    (previous, value, key) => {
      if (typeof value !== 'function') {
        throw new Error(`Selector '${key}' does not exist.`);
      }
      return { ...previous, [key]: value(state, ownProps) };
    },
    {},
  );

export default {
  // nested selectors
  params,
  chart,
  // selectors
  ...inputs,
  ...user,
  ...leaders,
  ...locationAutocomplete,
  average_footprint: state => state.average_footprint,
  user_footprint: state => state.user_footprint,
  ui: state => state.ui,
  results,
  isGlobalMenuVisible,
  isTokenValid,
  token,
  inputIncome,
  inputSize,
  locationModes,
  termsAndConditionsUrl,
  location,
  locationSuggestions,
  userTypedCanada,
  isFootprintInitialized,
  hasUserChangedInput,
  userFootprintInitialized,
  averageFootprintInitialized,
  resultTakeActionDollars,
  resultTakeActionTons,
  resultTakeActionNet10Yr,
  allSavings,
  totalDollarsSaved,
  totalTonsSaved,
  totalNet10YrCost,
  savings,
  isTravelUiSimple,
  isMetric,
  canAddMoreVehicles,
  vehicles,
  electricTradeInVehicle,
  hybridTradeInVehicle,
  altFuelTradeInVehicle,
  moreEfficientTradeInVehicle,
  rideMyBikeTradeInVehicle,
  publicTransportationTradeInVehicle,
  telecommuteTradeInVehicle,
  unitsDisplay,
  inputValue,
  unitValue,
  isMonthlyInput,
  unitOptions,
  annualElectricityConsumption,
  locale,
  widgetExists,
  widgetWindow,
  widgetContainer,
  widgetSource,
  widgetOptions,
  actionsTaken,
  modalDeleteUser,
  modalSaveUser,
  toast,
  averageComparisonLocation,
  spinner,
  pledges,
  modalLeaderPledges,
};
